import React, { FormEvent, useEffect, useState } from 'react'
import { emptyFormDTO, FormDTO, FormFieldDTO, fromModel } from '../../modules/forms/models/FormDTO'
import { getFormContainer } from '../../container/form-module'
import { FormService } from '../../modules/forms/services/FormService'
import { FORM_SERVICE_KEY } from '../../modules/forms'
import { Box, MenuItem, Select, FormControl, InputLabel } from '@mui/material'
import { TextFieldItem } from '../../components/form-card/TextFieldItem'
import { EditorFields } from './EditorFields'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { navigate } from '@reach/router'
import { ROUTE_FORM_GENERATOR } from '../../routes/routes-constants'
import { isSpecialType } from '../../common/utils/enums'
import { Alert } from '@material-ui/lab'
import { useTranslation } from 'react-i18next'
import style from './Editor.module.css'
import { Header } from '../../components/header/Header'
import { CronItemMockup } from 'components/form-card/CronItemMockup'
import { Cron, cronString } from '../../modules/forms/enums/Cron'
import { PreviewForm } from './PreviewForm'
import { FormCategory, CATEGORY_OPTIONS, getCategoryLabel } from '../../common/enums/FormCategories'
import { PageLayout } from 'pages/layout'

enum FormFieldsError {
  NO_FIELDS = 'formShouldHaveAtLeastAField',
  ORDER = 'formFieldsErrorOrder',
  NO_OPTIONS = 'formFieldsErrorNoOptions',
  CONSECUTIVE = 'formFieldsErrorConsecutive',
}

type EditorProps = {
  id?: string
}

const formContainer = getFormContainer()
const formService = formContainer.get<FormService>(FORM_SERVICE_KEY)

export function Editor(props: EditorProps) {
  const { t } = useTranslation()
  const cronTypes = cronString()

  const [form, setForm] = useState<FormDTO>(emptyFormDTO())
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isRecurrence, setIsRecurrence] = useState<boolean>(false)
  const [cron, setCron] = useState<string>(cronTypes[Cron.Empty])
  const [preview, setPreview] = useState<boolean>(false)
  const [dataPreview, setDataPreview] = useState<FormDTO>(emptyFormDTO())

  const handleChangeRecurrence = (s: string) => setCron(s)

  useEffect(() => {
    if (!props?.id) {
      return
    }
    formService.getByID(props.id).subscribe((res) => {
      if (res) {
        setForm(fromModel(res))
        console.log(JSON.stringify(form))
      }
    })
  }, [])

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(Object.assign({ ...form }, { [e.target.name]: e.target.value }))

  const handleCategoryChange = (e: any) => {
    setForm(Object.assign({ ...form }, { category: e.target.value }))
  }

  const changeFormFields = (formFields: FormFieldDTO[]) => (form.formFields = formFields)

  const saveForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!validateForm()) {
      return
    }

    const newFormFields: FormFieldDTO[] = []
    form.formFields.forEach((ff) => {
      newFormFields.push(Object.assign({ ...ff }, { formID: form.id, order: ff.order }))
    })
    const newForm = Object.assign(
      { ...form },
      {
        formFields: newFormFields,
        repeating: cron,
        category: form.category || FormCategory.OTHER,
      }
    )
    if (props.id) {
      formService.update(newForm).subscribe(() => goBack())
      return
    }
    formService.add(newForm).subscribe(() => goBack())
  }

  const validateForm = () => {
    const orders = form.formFields.map((f) => f.order)
    const hasDuplicateOrders = orders.some((order, index) => orders.indexOf(order) !== index)

    if (hasDuplicateOrders) {
      setErrorMessage(FormFieldsError.ORDER)
      return false
    }

    if (form.formFields.length > 0) {
      const sortedOrders = [...orders].sort((a, b) => a - b)
      const hasGaps = sortedOrders.some((order, index) => order !== index + 1)

      if (hasGaps) {
        setErrorMessage('formFieldsErrorConsecutive')
        return false
      }
    }

    // Validar campos especiales sin opciones
    if (
      form.formFields.some(
        (f) => isSpecialType(f.type) && (!f.optionValues || f.optionValues?.length === 0)
      )
    ) {
      setErrorMessage(FormFieldsError.NO_OPTIONS)
      return false
    }

    if (form.formFields.length === 0) {
      setErrorMessage(FormFieldsError.NO_FIELDS)
      return false
    }

    setErrorMessage('')
    return true
  }

  const goBack = () => navigate(ROUTE_FORM_GENERATOR)

  const showPreview = () => {
    const newFormFields: FormFieldDTO[] = []
    form.formFields.forEach((ff) => {
      newFormFields.push(Object.assign({ ...ff }, { formID: form.id, order: ff.order }))
    })
    const newForm = Object.assign(
      { ...form },
      {
        formFields: newFormFields,
        repeating: cron,
        category: form.category || FormCategory.OTHER,
      }
    )
    setDataPreview(newForm)
    setPreview(true)
  }

  return (
    <PageLayout>
      {preview && (
        <>
          <PreviewForm
            title={dataPreview.title}
            description={dataPreview.description}
            category={dataPreview.category}
            formFields={dataPreview.formFields}
            repeating={dataPreview.repeating}
            id=""
          />

          <Box mb={2} mt={2}>
            <strong>{t('category')}:</strong> {t(getCategoryLabel(dataPreview.category))}
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <AppButton
              theme={ButtonTheme.NewPrimary}
              type="button"
              label={t('closePreview')}
              handler={() => {
                setPreview(false)
              }}
            />
          </Box>
        </>
      )}
      {!preview && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
          <Box sx={{ flexShrink: 0 }}>
            <Header label={t('newForm')} />
          </Box>

          <form
            className={style.form}
            onSubmit={(e) => saveForm(e)}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}
          >
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <Box mb={3} className={style.box} display="flex">
                <b className={style.titleLabel}>{t('title')}*</b>
                <Box display="flex" className={style.textField}>
                  <TextFieldItem
                    field="title"
                    value={form.title}
                    label={''}
                    type={'text'}
                    handleChange={handleInput}
                    rows={undefined}
                    required={true}
                  />
                </Box>
              </Box>

              <Box mb={3} className={style.box} display="flex">
                <b className={style.descriptionLabel}>{t('description')}*</b>
                <Box display="flex" className={style.textField}>
                  <TextFieldItem
                    field="description"
                    value={form.description}
                    label={''}
                    type={'text'}
                    handleChange={handleInput}
                    rows={4}
                    required={true}
                  />
                </Box>
              </Box>

              <Box mb={3} className={style.box} display="flex">
                <b className={style.descriptionLabel} style={{ marginTop: '1.2%' }}>
                  {t('category')}*
                </b>
                <Box display="flex" className={style.textField}>
                  <FormControl fullWidth>
                    <InputLabel>{t('selectCategory')}</InputLabel>
                    <Select
                      value={form.category || FormCategory.OTHER}
                      onChange={handleCategoryChange}
                      label={t('selectCategory')}
                      required
                    >
                      {CATEGORY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {isRecurrence && (
                <Box mb={3}>
                  <CronItemMockup handleChange={handleChangeRecurrence} />
                </Box>
              )}

              <Box mb={3} className={style.box}>
                <EditorFields
                  formFields={form.formFields.sort((a, b) => a.order - b.order)}
                  handlerChange={changeFormFields}
                />
              </Box>

              {errorMessage && (
                <Box mb={3}>
                  <Alert severity="warning" key="errorMessage" id="errorMessage">
                    {t(errorMessage)}
                  </Alert>
                </Box>
              )}
            </Box>

            <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <AppButton
                theme={ButtonTheme.NewPrimary}
                type={'button'}
                label={t('previewForm')}
                handler={() => {
                  showPreview()
                }}
              />
              <div className={style.separator} />
              <AppButton
                theme={ButtonTheme.NewSecondary}
                type={'button'}
                label={t('cancel')}
                handler={goBack}
              />
              <div className={style.separator} />
              <AppButton
                theme={ButtonTheme.NewPrimary}
                type={'submit'}
                label={t('save')}
                handler={() => {}}
              />
            </Box>
          </form>
        </Box>
      )}
    </PageLayout>
  )
}
